<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'SetInvoiceVatPeriod',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoices: {
      type: Array,
      required: true,
      default: null,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const vatPeriods = ref(null);
  const item = ref({
    vat_period: null,
    lock_invoices: false,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    vatPeriods: vatPeriods.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_vatPeriods = async () => {
    return $app.value.repo.get('list_vat_periods');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'vatPeriods' },
      {
        name: 'item',
        cache: { type: 'local', key: 'SetInvoiceVatPeriod.item' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { vatPeriods: $$load_vatPeriods },
    ux: {},
    dataRefs: {
      vatPeriods,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('SetInvoiceVatPeriod', 'Set VAT period'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_gmfr68tkw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_adl78x7le_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: true,
      },
    };
  };
  const act_1040167445267876_request_jhp53rq5v_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_set_invoice_vat_period',
      data: {
        ...item.value,
        vat_period: { id: item.value.vat_period },
        invoices: props.invoices.map((p) => ({ id: p.id })),
      },
    };
  };

  const act_7315092382398562_closeModal_gmfr68tkw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_gmfr68tkw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_5apnfrqwo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_gmfr68tkw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_5apnfrqwo],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_vvsymxl41 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_va3skk4ws =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_vvsymxl41,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_va3skk4ws],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_o8tkfvx5x =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_dghogzv7y =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_o8tkfvx5x,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_dghogzv7y],
    event: 'close',
  });
  const act_1040167445267876_closeModal_adl78x7le =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_adl78x7le_getActionArgs,
    });
  const inst_evh_1040167445267876_success_1rltwuacq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_adl78x7le,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_jhp53rq5v = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_jhp53rq5v_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_1rltwuacq],
  });
  const inst_evh_1040167445267876_submit_5sxgd9t7l =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_jhp53rq5v,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_5sxgd9t7l],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_ry8j8aywi =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_dtvgl4mx3 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_ry8j8aywi,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_dtvgl4mx3],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_5apnfrqwo,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_va3skk4ws,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_dghogzv7y,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_1rltwuacq,
    inst_evh_1040167445267876_submit_5sxgd9t7l,
    submit: inst_evh_1040167445267876_submit_5sxgd9t7l,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_dtvgl4mx3,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_5apnfrqwo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_5apnfrqwo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_va3skk4ws = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_va3skk4ws.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_dghogzv7y = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_dghogzv7y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_1rltwuacq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_1rltwuacq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_5sxgd9t7l = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_5sxgd9t7l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_dtvgl4mx3 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_dtvgl4mx3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="SetInvoiceVatPeriod"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="createOptions"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="vat_period"
          row
          :label-cols-md="0"
          :content-cols-md="12"
          required
          :label="pgettext('SetInvoiceVatPeriod', 'VAT Period')"
        >
          <ItpFormSelect2
            name="vat_period"
            v-model="item.vat_period"
            :value="item.vat_period"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.vat_period =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="vatPeriods"
            valueField="id"
            textField="name"
            required
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="lock_invoices"
          :description="
            pgettext(
              'SetInvoiceVatPeriod',
              'Lock invoices in order to prevent further modifications'
            )
          "
          row
          :label-cols-md="0"
          :content-cols-md="12"
        >
          <ItpFormCheckbox
            name="lock_invoices"
            v-model="item.lock_invoices"
            :value="item.lock_invoices"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.lock_invoices =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="pgettext('SetInvoiceVatPeriod', 'Lock invoices')"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.createOptions && forms.createOptions.id"
          :text="pgettext('SetInvoiceVatPeriod', 'Submit')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('SetInvoiceVatPeriod', 'Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
